import React, { memo, useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { Checkbox, FormControlLabel } from '@mui/material';
import styles from '@/components/CampaignHeader/CampaignHeader.module.scss';
import CloseCampaignDialog from '@/components/CampaignHeader/CloseCampaignDialog/CloseCampaignDialog';
import SummaryButton from '@/components/CampaignHeader/SummaryButton/SummaryButton';
import DialogEditTitle from '@/components/DialogEditTitle/DialogEditTitle';
import DialogSearch from '@/components/DialogSearch/DialogSearch';
import HighlightText from '@/components/HighlightText/HighlightText';
import TabsComponent from '@/components/TabsComponent/TabsComponent';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import { IcoButtonSimple } from '@/components/UI/IcoButton/IcoButton';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import TooltipComponent from '@/components/UI/Tooltip/TooltipComponent';
import UniversalChip from '@/components/UI/UniversalChip/UniversalChip';
import { isViaPlants, mapAffectedVehicle } from '@/containers/CampaignDetail/AffectedVehicles/SeriesPanel/SeriesRow/series-row-utils';
import { validateTranslationsForm } from '@/helpers/campaignValidations';
import {
  stateTabs, TEAMS_PERSON, chipTypes, campaignTabs, campaignTabsToIndex, statusCampaign as CR_STATUS, errorTypes, ROUTES,
  STORAGE, ENGLISH, GERMAN, ADMIN, CREATOR as CREATOR_ROLE, SUCCESS, CREATOR_EXTERNAL, ERROR,
  HELP_AREA,
} from '@/helpers/constants';
import { CR_PERMISSION_ACTIONS } from '@/helpers/crPermissions';
import { formatSummaryUser, getErrorCode, getVersionUrlParam, isFieldDisabledBYPRRD, isUserInRecipients, tabLowerCase } from '@/helpers/utils';
import useApiRequest from '@/hooks/useApiRequest';
import useAuth from '@/hooks/useAuth';
import useCampaignValidate from '@/hooks/useCampaignValidate';
import useCRPermissionMatrix from '@/hooks/useCRPermissionMatrix';
import useDownloadFile from '@/hooks/useDownloadFile';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useGoTo from '@/hooks/useGoTo';
import useSave from '@/hooks/useSave';
import useToastContext from '@/hooks/useToastContext';
import useVisibilityMatrix from '@/hooks/useVisibilityMatrix';
import {
  setCampaignHelp, setOriginalValues, setCampaign, setValidations, setCampaignRecipients, setCampaignClassification,
  toggleCampaignHistory, setCampaignVehicles, setCampaignTranslations, setTranslations, setCampaignHardware,
  setLimitationPlants, setCampaignChanges, setCampaignInfo,
} from '@/redux/Campaign/campaign.action';
import {
  getCampaign, getOriginalValues, getCampaignValidations, getCampaignInfo, getCampaignRecipients, getVolumeHeaderPlants,
  getCampaignVehiclesSeries, getCampaignVehicles, getCampaignChanges, getCampaignDescription, getIsVANCR, getSelectedPlants,
} from '@/redux/Campaign/campaign.reducer';
import { setHeaderLanguage } from '@/redux/Header/header.action';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import { getSettingsInfo } from '@/redux/User/user.reducer';
import { getWorkflow } from '@/redux/Workflow/workflow.reducer';
import campaignServices from '@/services/campaignServices';
import usersServices from '@/services/usersServices';
import {
  AffectedVehicles, Campaign, CampaignChangesFromVersion, CampaignInfo, HelpTextState, Language, LanguageValidations,
  LimitationSerie, Description, OriginalValues, Recipients, Validations, Workflow, LimitationPlant,
} from '@/types';
import { StatusCampaign, StatusSubCampaign, VisibilityMatrixAction, VisibilityMatrixFeature } from '@/types/Matrix';

const filterUnUsedFields = (fields: string[]) => (fields
  ? fields
    .filter((f) => !['version', 'lastModification', 'commentsRdCampaignManager', 'commentsApproverCauser'].includes(f))
  : []);

type CampaignHeaderProps = {
  isLoading: boolean;
  reloadCR: () => void;
  campaignInfo: CampaignInfo;
  campaign: Campaign;
  recipientsInfo: Recipients;
  setCR: (campaign: Partial<Campaign>) => void;  // eslint-disable-line
  campaignHelp: HelpTextState;
  setHelp: (help: HelpTextState) => void;
  setHeaderLang: (lang: string) => void;
  campaignOriginalValues: OriginalValues;
  setOriginValues: (values: OriginalValues) => void;
  validations: Validations;
  setFormsValidations: (validations: Validations) => void;
  toggleHistory: (show: boolean) => void;
  seriesAF: LimitationSerie[];
  headerLanguage: string;
  workflow: Workflow;
  setAffectedVehicles: (vehicles: AffectedVehicles) => void;
  affectedVehicles: AffectedVehicles;
  setLimitPlants: (limitationPlants: LimitationPlant[]) => void;
  headerPlants: LimitationPlant[];
  setCRChanges: (changes: CampaignChangesFromVersion) => void;
  campaignChanges: CampaignChangesFromVersion;
  campaignDescription: Description;
  isVANCR: boolean;
  setRecipients: (recipients: Recipients) => void;
};

const CampaignHeader: React.FC<CampaignHeaderProps> = (props) => {
  const { id, tab, version: urlVersion } = useParams<{ id: string; tab: string; version: string }>();
  const { isLoading, reloadCR, campaignInfo, campaign, recipientsInfo, setCR, campaignHelp, setHelp, setHeaderLang,
    campaignOriginalValues, setOriginValues, validations, setFormsValidations, toggleHistory, seriesAF, setRecipients,
    headerLanguage, workflow, affectedVehicles, setAffectedVehicles, setLimitPlants, headerPlants, setCRChanges,
    campaignChanges, campaignDescription, isVANCR } = props;
  const version = parseInt(urlVersion!, 10) || campaignInfo?.version;
  const { t } = useTranslation();
  const authService = useAuth();
  const { canSee } = useVisibilityMatrix();
  const { isButtonDisabled, isReadOnly } = useCRPermissionMatrix(campaignInfo?.status, campaignInfo?.authorization);
  const canSeeCR = (action: VisibilityMatrixAction) => canSee({
    feature: VisibilityMatrixFeature.CAMPAIGN_REQUEST,
    action: action,
    status: campaignInfo?.status as StatusCampaign | StatusSubCampaign, // TODO Missing type on status
  }) || !isButtonDisabled(action as unknown as CR_PERMISSION_ACTIONS);
  const [openDialogEditTitle, setOpenDialogEditTitle] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openCloseCampaignDialog, setOpenCloseCampaignDialog] = useState(false);
  const { addToast, closeValidationToast } = useToastContext();
  const [userStandins, setUserStandins] = useState();
  const [showTranslationsDialog, setShowTranslationsDialog] = useState(false);
  const [checkDoNotShowAgain, setCheckDoNotShowAgain] = useState(false);
  const [isCreatingPdfSpinner, setIsCreatingPdfSpinner] = useState(false);
  const [openAssignUserDialog, setOpenAssignUserDialog] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const translationLang = campaignInfo?.defaultLanguage?.toUpperCase() === ENGLISH ? GERMAN : ENGLISH;
  const save = useSave(tabLowerCase(tab));
  const { downloadPDFCR } = useDownloadFile();
  const { loading, setLoading } = useGlobalSpinnerContext();
  const { active: showChangesActive } = campaignChanges || {};
  const canEdit = !isLoading && !isButtonDisabled(CR_PERMISSION_ACTIONS.SAVE) && (headerLanguage === campaignInfo?.defaultLanguage
    || (headerLanguage !== campaignInfo?.defaultLanguage
      && ['translations', 'plant-feedback'].includes(tabLowerCase(tab))));
  const canEditDelete = !isLoading && !isButtonDisabled(CR_PERMISSION_ACTIONS.DELETE) && (headerLanguage === campaignInfo?.defaultLanguage
    || (headerLanguage !== campaignInfo?.defaultLanguage
      && ['translations', 'plant-feedback'].includes(tabLowerCase(tab))));
  const checkSaveDisabled = isReadOnly() || isButtonDisabled(CR_PERMISSION_ACTIONS.SAVE) || !campaignOriginalValues?.hasChange;
  const checkEditTitleDisabled = isReadOnly() || isButtonDisabled(CR_PERMISSION_ACTIONS.EDIT_TITLE) || (headerLanguage !== campaignInfo?.defaultLanguage) || isFieldDisabledBYPRRD(campaignInfo, 'editTitle');
  const isSummary = useMemo(() => ['summary', 'plant-feedback'].includes(tabLowerCase(tab)), [tab]);
  const isUserStandIn = (standIns, user) => standIns?.some((standIn) => standIn.standInId === user);
  const canDeactivate = !isLoading && campaignInfo?.status === CR_STATUS.IN_CREATION;
  const userIsRecipient = isUserInRecipients(campaignInfo?.recipients, authService.userCode);
  const checkUserPermission = (campaign.recipients?.responsibleRequester?.code === authService.userCode)
    || (campaignInfo?.creationUser?.code === authService.userCode)
    || recipientsInfo?.standIn?.code === authService.userCode;
  const validateCampaignForm = useCampaignValidate();
  const showChangesDisabled = loading || campaignInfo?.version === 1 || isButtonDisabled(CR_PERMISSION_ACTIONS.SHOW_CHANGES);
  const { goTo } = useGoTo();
  const isCreatorCanDelete = authService?.checkRoles([CREATOR_ROLE]) && campaignInfo?.status !== CR_STATUS.IN_CREATION;
  const isAdminCompleted = authService?.checkRoles([ADMIN]) && campaignInfo?.status === CR_STATUS.COMPLETED;
  const isStandInOrAssignedUser = campaignInfo?.assignedUser?.code === authService.userCode || isUserStandIn(userStandins, authService.userCode);
  const hideSummaryButton = campaignInfo?.status === CR_STATUS.IN_REVIEW && !isStandInOrAssignedUser;
  const showFullCRConditions = isSummary && !hideSummaryButton && (canSeeCR(VisibilityMatrixAction.FULL_CR) || userIsRecipient);

  const checkDeleteDisabled = () => {
    if (authService?.checkRoles([ADMIN, CREATOR_ROLE])) {
      return (isCreatorCanDelete || isAdminCompleted);
    }

    return isButtonDisabled(CR_PERMISSION_ACTIONS.DELETE) || (campaignInfo?.version !== 1);
  };

  const resetCampaignOriginalValues = () => {
    setCR({ [stateTabs[tabLowerCase(tab)]]: campaignOriginalValues.values });
    setFormsValidations(campaignHelp?.validations as Validations);

    if (campaignOriginalValues) {
      campaignOriginalValues.hasChange = false;
    }
    const values = {
      ...campaignOriginalValues,
      hasChange: false,
    };
    setOriginValues(values);
  };

  const handleSaveCampaignSuccess = () => {
    setOriginValues({ values: campaign[stateTabs[tabLowerCase(tab)]], hasChange: false });
    setHelp({ ...campaignHelp, validations });
  };

  const handleSaveCampaignError = (err) => {
    const errorCode = getErrorCode(err);

    if (errorCode === errorTypes.SFM_NUMBER_LENGTH_ERROR) {
      addToast(t('campaign.sfmWrongLength'), 'ERROR');

      return;
    }

    addToast(err?.message, 'ERROR');
  };

  const checkEnableSave = () => {
    if (campaignInfo?.authorization?.kindOfPermission === ADMIN) {
      return !([CR_STATUS.COMPLETED, CR_STATUS.DELETED, CR_STATUS.REQUESTED_FOR_CHANGES] as string[]).includes(campaign?.info?.status);
    }

    return campaignInfo?.status === CR_STATUS.IN_CREATION;
  };

  const saveCampaign = async () => {
    if (isSaving) return;

    if (canEdit && tabLowerCase(tab) !== 'attachments' && checkEnableSave()) {
      setIsSaving(true);
      setLoading(true);
      try {
        await save();
        handleSaveCampaignSuccess();
      } catch (err) {
        handleSaveCampaignError(err);
      } finally {
        setLoading(false);
        setIsSaving(false);
      }
    }
  };

  const fetchStandins = useApiRequest(usersServices.getStandIns);

  const findUserStandins = useCallback((userCode) => {
    fetchStandins(userCode)
      .then((response) => setUserStandins(response?.standInListSingleResponseByUserId || []))
      .catch((error) => {
        if (error.name !== errorTypes.ABORT_ERROR) addToast(error.message, 'ERROR');
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const saveChanges = () => campaignOriginalValues?.hasChange && saveCampaign();

  const languageSelectorLabels = () => {
    const nonDefault = campaignInfo?.activeLanguages?.filter((active) => active.languageCode !== campaignInfo?.defaultLanguage)[0];
    let label = t('campaign.hideTranslation', { lang: t(`languageChip.${nonDefault?.languageCode}`) });

    if (campaignInfo?.defaultLanguage === headerLanguage) {
      label = t('campaign.showTranslation', { lang: t(`languageChip.${nonDefault?.languageCode}`) });
    }

    return label;
  };

  useEffect(() => {
    if (campaignInfo?.assignedUser?.code) {
      findUserStandins(campaignInfo.assignedUser?.code);
    }
  }, [campaignInfo?.assignedUser, findUserStandins]);

  useEffect(() => {
    if (!window.location.pathname.toLowerCase().includes('/campaign/') && campaign.originalValues?.hasChange) {
      resetCampaignOriginalValues();
    }
  }, [campaignOriginalValues?.hasChange]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (campaign.originalValues?.hasChange) {
      window.addEventListener('beforeunload', save);

      return () => {
        window.removeEventListener('beforeunload', save);
      };
    }
  }, [campaign]);// eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    localStorage.setItem(STORAGE.PREVIOUS_TAB, tabLowerCase(tab));
    const intervalSaving = !canEdit
      ? null
      : setInterval(() => {
        saveCampaign();
      }, 300000);

    setHeaderLang(campaignInfo?.defaultLanguage);

    return () => {
      if (intervalSaving) {
        clearInterval(intervalSaving);
      }
    };
  }, []);// eslint-disable-line react-hooks/exhaustive-deps

  const fetchAffectedVehicles = useApiRequest(campaignServices.getCampaignRetryWithSignal);

  useEffect(() => {
    const fetchAffVehicles = async () => {
      const affVehiclesInfo: AffectedVehicles = await fetchAffectedVehicles(id, 'affected-vehicles', version);
      setAffectedVehicles(mapAffectedVehicle(affVehiclesInfo));
    };
    fetchAffVehicles();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteCampaingsToRecycleBin = useApiRequest(campaignServices.deleteCampaignsToRecycleBin);

  const deleteCampaign = () => {
    if (canEditDelete) {
      deleteCampaingsToRecycleBin([id])
        .then(() => {
          goTo(ROUTES.DASHBOARD);
          addToast(t('alert.removingSuccess'), SUCCESS);
        })
        .catch((error) => {
          if (error.name !== errorTypes.ABORT_ERROR) addToast(error.message, ERROR);
        });
    }
  };

  const updateTitle = () => {
    setOpenDialogEditTitle(false);
    reloadCR();
  };

  const text = (label) => (isLoading ? '-----' : label);

  const showContactPersonInfo = (user) => {
    window.open(`${TEAMS_PERSON}${user}`, '_blank');
  };

  const goBack = async () => {
    setLoading(true);

    if (campaign.originalValues?.hasChange) {
      await saveCampaign();
    }
    goTo(ROUTES.CR_OVERVIEW);
  };

  const showHelp = () => {
    setHelp({
      ...campaignHelp,
      show: !campaignHelp?.show,
    });
  };

  const onClosePopup = (reload = false) => {
    setOpenCloseCampaignDialog(false);

    if (reload) reloadCR();
  };

  const validateCampaign = async () => {
    let isCampaingFormValid = false;

    try {
      isCampaingFormValid = await validateCampaignForm();
    } catch (err: any) {
      addToast(err.message, ERROR);
      setLoading(false);
    }

    if (isCampaingFormValid) {
      localStorage.setItem(STORAGE.PREVIOUS_TAB, 'summary');
      goTo(`/campaign/${id}${getVersionUrlParam(version)}/summary`);
      closeValidationToast();
    }
  };

  const validateSummaryTranslations = () => {
    let newState: Validations = { ...validations, active: validations?.active, all: true };

    const languagesValidations = campaignInfo?.activeLanguages
      .map(({ languageCode }) => {
        if (languageCode !== campaignInfo?.defaultLanguage) {
          newState = {
            ...newState,
            translations: {
              ...newState?.translations as LanguageValidations,
              [languageCode]: validateTranslationsForm(campaign, languageCode),
            },
          };
        }

        const isValid = newState.translations ? Object.keys(newState.translations).every((langCode) => !newState.translations?.[langCode]?.invalid) : true;

        return { language: languageCode, isValid };
      });

    setFormsValidations(newState);

    return languagesValidations?.every(({ isValid }) => isValid);
  };

  const showSummary = async () => {
    setLoading(true);

    if (campaignInfo?.isTranslationUpdateNeeded) {
      setCampaignInfo({ ...campaignInfo, isTranslationUpdateNeeded: false }, false);
    }

    if (campaignOriginalValues?.hasChange) {
      await saveCampaign();
      await validateCampaign();
    } else if (campaignInfo?.authorization?.readOnly) {
      goTo(`/campaign/${id}${getVersionUrlParam(version)}/summary`);
    } else {
      await validateCampaign();
    }
    setLoading(false);
  };

  const print = async () => {
    setIsCreatingPdfSpinner(true);
    await saveCampaign();
    await downloadPDFCR(id, headerLanguage);
    setIsCreatingPdfSpinner(false);
  };

  const canCloseCampaign = () => {
    try {
      return canDeactivate && (checkUserPermission || (isUserStandIn(userStandins, authService.userCode)));
    } catch (e) {
      // test
    }
  };

  const assignCreatorCR = (selectedUser) => {
    setOpenAssignUserDialog(false);
    setLoading(true);
    const user = selectedUser[0];
    const data = { code: user.userCode, summary: formatSummaryUser(user) };
    campaignServices.putAssignCreatorCR(campaignInfo?.id, data.code).then(() => {
      if (authService.isAdmin) {
        const newUserInfo = {
          code: data.code,
          summary: data.summary,
          department: user.department || '',
        };
        const updatedRecipients = {
          ...recipientsInfo,
          responsibleRequester: newUserInfo,
        };
        setRecipients(updatedRecipients);
        reloadCR();
        setLoading(false);
      } else {
        setTimeout(() => {
          goTo(ROUTES.CR_OVERVIEW);
        }, 1000);
      }
      addToast(t('dialog.assignUserSuccess', { newCreator: data.summary }), SUCCESS);
    })
      .catch((error) => {
        addToast(error.message, ERROR);
        setLoading(false);
      });
  };

  useEffect(() => {
    const mustShowDialog = campaignInfo?.defaultLanguage?.length > 1
      && headerLanguage !== campaignInfo?.defaultLanguage
      && localStorage.getItem(STORAGE.TRANSLATION_DIALOG_SHOW) !== 'true';
    setShowTranslationsDialog(mustShowDialog);
  }, [headerLanguage, campaignInfo?.defaultLanguage]);

  const onCloseTranslationInfoDialog = () => {
    localStorage.setItem(STORAGE.TRANSLATION_DIALOG_SHOW, checkDoNotShowAgain ? 'true' : 'false');
    setShowTranslationsDialog(false);
  };

  const doNotShowAgainCheck = (
    <FormControlLabel
      control={(
        <Checkbox
          data-testId="cb-do-not-show-again"
          color="primary"
          checked={checkDoNotShowAgain}
          onChange={() => setCheckDoNotShowAgain(!checkDoNotShowAgain)}
        />
      )}
      label={t('translations.translationInfoCheck')}
    />
  );

  const tabsValues = (tabSelected) => {
    const translationsHidden = campaignInfo?.activeLanguages?.length <= 1 || isButtonDisabled(CR_PERMISSION_ACTIONS.TRANSLATIONS_TAB);
    const visibleTabs = campaignTabs.filter((navTab) => !(navTab.label === HELP_AREA.TRANSLATIONS && translationsHidden));
    const index = visibleTabs.findIndex((tab) => tab.link === tabSelected);

    return index >= 0 ? index : 0;
  };

  const handleNavClick = async (path) => {
    if (campaign.originalValues?.hasChange) {
      if (tabLowerCase(tab) !== 'attachments') {
        await saveCampaign();
      }
      setHelp({ ...campaignHelp, focus: null });
    }
    localStorage.setItem(STORAGE.PREVIOUS_TAB, tabLowerCase(path.split('/').pop()));
    goTo(path);
  };

  const showFullCR = async () => {
    if (campaign.originalValues?.hasChange) {
      await saveCampaign();
    }

    goTo(`/campaign/${id}${getVersionUrlParam(version)}/description`);
  };

  const fecthCampaingChanges = useApiRequest(campaignServices.getCampaignChanges);

  const showChangesCampaign = async () => {
    setLoading(true);
    await saveChanges();

    if (!campaignChanges?.versionToCompare || (canEdit && !campaignChanges?.active)) {
      const changesOfPreviousVersion = await fecthCampaingChanges(id, (version - 1), version);
      const campaignChangesData = changesOfPreviousVersion?.differencesByModule;
      setCRChanges({
        versionToCompare: version - 1,
        active: !campaignChanges?.active,
        description: filterUnUsedFields(campaignChangesData?.DESCRIPTION),
        classification: filterUnUsedFields(campaignChangesData?.CLASIFICATION),
        recipients: filterUnUsedFields(campaignChangesData?.RECIPIENTS),
        affectedVehicles:
          [...filterUnUsedFields(campaignChangesData?.AFFECTED_VEHICLES), ...filterUnUsedFields(campaignChangesData?.LIMITATION_VOLUMES)],
        affectedSoftware: filterUnUsedFields(campaignChangesData?.AFFECTED_SOFTWARE),
        affectedHardware: filterUnUsedFields(campaignChangesData?.AFFECTED_HARDWARE),
      });
    } else {
      setCRChanges({ ...campaignChanges, active: !campaignChanges?.active });
    }
    setLoading(false);
  };

  const handleClickShowTranslation = () => {
    setHeaderLang(headerLanguage === ENGLISH ? GERMAN : ENGLISH);
    saveChanges();
  };

  const toggleHistoryMenu = () => {
    saveChanges();
    toggleHistory(!campaign?.history?.show);
  };

  const getDependencesOfSavingAffVev = () => (isViaPlants(affectedVehicles) ? headerPlants : seriesAF);

  useEffect(() => {
    const saveMS = async () => {
      if ((campaign.originalValues?.changesIn?.plants || campaign.originalValues?.changesIn?.series)
        && ((isViaPlants(affectedVehicles) && !loading) || !isViaPlants(affectedVehicles))) {
        await saveChanges();

        if (!isViaPlants(affectedVehicles) && headerPlants?.length > 0) {
          const limitationPlants = {};
          affectedVehicles.affectedPlants?.forEach((plant) => {
            limitationPlants[plant.wkzNumber] = plant;
          });
          setLimitPlants(limitationPlants);
        }
      }
    };
    saveMS();
  }, [getDependencesOfSavingAffVev()]); // eslint-disable-line react-hooks/exhaustive-deps

  // reload CR info when description source dropdown change, its for DMP/TSP fixed in the title if doorman is selected
  useEffect(() => {
    const saveDS = async () => {
      if (campaign.originalValues?.hasChange) {
        await saveChanges();
        reloadCR();
      }
    };
    saveDS();
  }, [campaignDescription?.source]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`${styles.Header} ${isLoading ? styles.Loading : ''}`} id="campaign_header">
      <div className={styles.HeaderInfo}>
        <div className={`${styles.Flex} ${styles.TitleHeader}`}>
          <div className={`${styles.Flex} ${styles.TitleHeaderLeft}`}>
            <IcoButtonSimple
              id="btn-exit-campaign"
              icon="icon-pfeil-chevron-links"
              tooltipText={t('campaign.backToOverview')}
              handleClick={goBack}
            />
            <TooltipComponent title={text(campaignInfo?.id)}>
              <span className={styles.CampaignCode}>{text(campaignInfo?.id)}</span>
            </TooltipComponent>
            <TooltipComponent title={text(campaignInfo?.title && campaignInfo?.title[headerLanguage])}>
              <span className={styles.CampaignTitle}>
                <HighlightText
                  isTitle
                  fieldId="header-title"
                  fieldPath={[]}
                  fieldType="read-only"
                >
                  {text(campaignInfo?.title && (campaignInfo?.title[headerLanguage]))}
                </HighlightText>
              </span>
            </TooltipComponent>
            {canSeeCR(VisibilityMatrixAction.EDIT_TITLE) && (
              <IcoButtonSimple
                disabled={checkEditTitleDisabled}
                id="btn-edit-campaign"
                icon="icon-stift-bearbeiten"
                tooltipText={t('campaign.editTitle')}
                handleClick={() => setOpenDialogEditTitle(true)}
              />
            )}
          </div>
          <div className={styles.Flex}>
            {showFullCRConditions && (
              <SimpleButton
                id="show-full-cr-btn"
                disabled={isButtonDisabled(CR_PERMISSION_ACTIONS.FULL_CR)}
                color="primary"
                buttonText={t('buttonsHeader.showFullCR')}
                handleClick={showFullCR}
              />
            )}
            {canCloseCampaign() && (
              <SimpleButton
                id="deactivate-cr-btn"
                color="primary"
                buttonText={t('buttonsHeader.closeCR')}
                handleClick={() => setOpenCloseCampaignDialog(true)}
              />
            )}
            {canSeeCR(VisibilityMatrixAction.SHOW_CHANGES) && (
              <IcoButtonSimple
                disabled={showChangesDisabled}
                id="btn-show-changes-campaign"
                icon={`icon-kreis-entscheid-gelb ${showChangesDisabled ? '' : 'Orange'}`}
                tooltipText={t('campaign.showChanges')}
                handleClick={showChangesCampaign}
                active={showChangesActive}
              />
            )}
            {loading
              ? (<div className={styles.SpinnerHeader}><Spinner small isVisible /></div>)
              : canSeeCR(VisibilityMatrixAction.SAVE) && (
                <IcoButtonSimple
                  disabled={checkSaveDisabled}
                  id="btn-save-campaign"
                  icon="icon-diskette-speichern"
                  tooltipText={t('campaign.save')}
                  handleClick={saveCampaign}
                />
              )}
            {canSeeCR(VisibilityMatrixAction.DELETE) && (
              <IcoButtonSimple
                disabled={checkDeleteDisabled()}
                id="btn-delete-campaign"
                icon="icon-muelleimer-loeschen"
                tooltipText={t('campaign.deleteCampaignRequest')}
                handleClick={() => setOpenDeleteDialog(true)}
              />
            )}
            <div>
              {isCreatingPdfSpinner && (<div data-testId="print-spinner" className={styles.SpinnerHeader}><Spinner small isVisible /></div>)}
              {!isCreatingPdfSpinner && (
                <IcoButtonSimple
                  id="btn-print-campaign"
                  icon="icon-drucken"
                  tooltipText={t('campaign.print')}
                  handleClick={print}
                />
              )}
            </div>
            <IcoButtonSimple
              id="btn-help-campaign"
              icon="icon-hilfe"
              tooltipText={t('campaign.showHelp')}
              handleClick={showHelp}
              active={campaignHelp?.show}
            />
          </div>
        </div>
        <div className={styles.SubTitleHeader}>
          <div>{text(t(`campaign.${campaignInfo?.status}`))}</div>
          <UniversalChip
            chipType={chipTypes.WITHOUT_FUNCTION}
            text={affectedVehicles?.campaignType}
          />
          <UniversalChip
            chipType={chipTypes.WITH_FUNCTION}
            text={`${t('campaignRequests.version')}: ${campaign.info.version}`}
            onClick={toggleHistoryMenu}
            active={campaign?.history?.show}
          />
          <UniversalChip
            chipType={chipTypes.WITH_FUNCTION}
            onClick={() => showContactPersonInfo(campaignInfo?.contactPerson?.code)}
            fieldPath={['creationUser', 'summary']}
            icon={<i className="di icon-person" />}
            text={text(campaignInfo?.contactPerson?.summary)}
          />
          <UniversalChip
            key="request-language"
            id="request-language"
            chipType={chipTypes.WITHOUT_FUNCTION}
            text={t('campaign.languagePill', { lang: t(`languageChip.${campaignInfo?.defaultLanguage}`) })}
          />
          {campaignInfo?.activeLanguages?.length > 1 && (
            <UniversalChip
              key="lang-button"
              id="lang-button"
              onClick={handleClickShowTranslation}
              chipType={chipTypes.WITH_FUNCTION}
              text={languageSelectorLabels()}
              active={headerLanguage !== campaignInfo?.defaultLanguage}
              dot="Alert"
              icon={<i className="di icon-weltkugel" />}
            />
          )}
        </div>
        <div className={styles.TabHeader}>
          <TabsComponent
            handleNavClick={handleNavClick}
            isSummary={isSummary}
            tabSelected={tabsValues(tabLowerCase(tab))}
          />
        </div>
      </div>
      <div className={styles.ButtonsContainer}>
        {!hideSummaryButton
          && (isSummary
            ? (
              <SummaryButton
                skippable={workflow?.skippableApproval}
                campaign={campaign}
                campaignId={id}
                reloadCR={reloadCR}
                campaignStatus={campaignInfo?.status}
                campaignVersion={campaignInfo?.version}
                validateCampaignForm={validateCampaignForm}
                validateSummaryTranslations={validateSummaryTranslations}
                saveCampaign={saveCampaign}
                campaignHasChanges={campaignOriginalValues?.hasChange || false}
                isVANCR={isVANCR}
              />
            )
            : (canSeeCR(VisibilityMatrixAction.SHOW_SUMMARY) || userIsRecipient) && (
              <>
                {((version === 1 && canSeeCR(VisibilityMatrixAction.ASSIGN_CREATOR)) && (
                  <Button
                    id="buttonAssignCreatorCR"
                    disabled={(isLoading || isButtonDisabled(CR_PERMISSION_ACTIONS.ASSIGN_CREATOR))}
                    handleClick={() => setOpenAssignUserDialog(true)}
                    buttonStyle="SecondaryRoundButton"
                    btnText={t('buttonsHeader.assignCreatorCR')}
                  />
                ))}
                <Button
                  id="buttonShowSummary"
                  disabled={isLoading || isButtonDisabled(CR_PERMISSION_ACTIONS.SHOW_SUMMARY)}
                  handleClick={showSummary}
                  buttonStyle="RoundButton"
                  btnText={t('buttonsHeader.showSummary')}
                />
              </>
            ))}
      </div>
      {openDialogEditTitle && <DialogEditTitle editCR={campaignInfo} close={updateTitle} />}
      {openDeleteDialog && (
        <ConfirmDialog
          close={() => setOpenDeleteDialog(false)}
          confirm={deleteCampaign}
          title={t('campaign.deleteCampaignTitle')}
          message={t('campaign.deleteCampaignMessage', { campaignId: campaignInfo?.id })}
          closeBtnText={t('dialog.cancel')}
          confirmBtnText={t('dialog.delete')}
          doubleConfirmationNeeded={{
            instructions: t('dialog.confirmCampaignDeletion.instructions'),
            expectedInput: t('dialog.confirmCampaignDeletion.expectedInput'),
          }}
        />
      )}
      {openCloseCampaignDialog && (
        <CloseCampaignDialog
          campaignInfo={campaignInfo}
          onClosePopup={onClosePopup}
          lang={translationLang as Language}
        />
      )}
      {showTranslationsDialog && (
        <ConfirmDialog
          type="information"
          confirm={onCloseTranslationInfoDialog}
          title={t('translations.translationInfoTitle')}
          message={t('translations.translationInfoMessage')}
          confirmBtnText={t('dialog.ok')}
        >
          {doNotShowAgainCheck}
        </ConfirmDialog>
      )}
      {openAssignUserDialog && (
        <DialogSearch
          // eslint-disable-next-line jsx-a11y/aria-role
          role={[CREATOR_ROLE.toUpperCase(), CREATOR_EXTERNAL.toUpperCase()]}
          title={t('buttonsHeader.assignCreatorCR')}
          close={() => setOpenAssignUserDialog(false)}
          updateOptionSelected={assignCreatorCR}
          isRemote={false}
          assignedUser={campaignInfo?.creationUser?.code}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  campaign: getCampaign(state),
  recipientsInfo: getCampaignRecipients(state),
  campaignOriginalValues: getOriginalValues(state),
  validations: getCampaignValidations(state),
  seriesAF: getCampaignVehiclesSeries(state),
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  headerPlants: getVolumeHeaderPlants(state),
  timeZone: getSettingsInfo(state)?.timezone,
  settingsInfo: getSettingsInfo(state),
  campaignInfo: getCampaignInfo(state),
  workflow: getWorkflow(state),
  affectedVehicles: getCampaignVehicles(state),
  affectedPlants: getSelectedPlants(state),
  campaignChanges: getCampaignChanges(state),
  campaignDescription: getCampaignDescription(state),
  isVANCR: getIsVANCR(state),
});

const actionCreators = {
  setHelp: setCampaignHelp,
  setOriginValues: setOriginalValues,
  setCR: setCampaign,
  setFormsValidations: setValidations,
  setRecipients: setCampaignRecipients,
  setClassification: setCampaignClassification,
  setAffectedVehicles: setCampaignVehicles,
  setTrans: setTranslations,
  setHeaderLang: setHeaderLanguage,
  toggleHistory: toggleCampaignHistory,
  setTransData: setCampaignTranslations,
  setHardware: setCampaignHardware,
  setLimitPlants: setLimitationPlants,
  setCRChanges: setCampaignChanges,
};

export default memo(connect(mapStateToProps, actionCreators)(CampaignHeader as any),
  (prev: any, next: any) => (
    prev.isLoading === next.isLoading
    && JSON.stringify(prev.campaignHelp) === JSON.stringify(next.campaignHelp)
    && JSON.stringify(prev.campaignInfo) === JSON.stringify(next.campaignInfo)
    && JSON.stringify(prev.validations) === JSON.stringify(next.validations)
  ));
